import React, { useState, useEffect } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container, Stack, Typography, Breadcrumbs, Box,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Button, Chip, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DownloadRounded,
  CheckCircle, Cancel,
} from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  quarantine: "warning",
  released:   "success",
  consumed:   "info",
};

function exportCSV(jobId, ledger) {
  const header = [
    "Heat Number", "Grade", "Shape", "Supplier",
    "Received Qty", "Unit", "Issued Pieces",
    "CMTR Verified", "Status", "Received Date", "Released At",
  ];
  const rows = ledger.map((h) => [
    h.heatNumber, h.grade, h.shape || "", h.supplier || "",
    h.received_qty, h.unit || "",
    h.issued_piece_count,
    h.cmtrVerified ? "Yes" : "No",
    h.status,
    h.receivedDate || "",
    h.releasedAt || "",
  ]);

  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: `heat-ledger-job${jobId}.csv` });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── component ──────────────────────────────────────────────────────────────

export default function HeatLedgerReport() {
  const navigate = useNavigate();

  const [jobs,    setJobs]    = useState([]);
  const [jobId,   setJobId]   = useState("");
  const [ledger,  setLedger]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // load job list
  useEffect(() => {
    ApiCall.get("/job")
      .then((r) => setJobs(r.data?.data?.jobs || []))
      .catch(() => setError("Failed to load jobs"));
  }, []);

  // fetch ledger when jobId changes
  useEffect(() => {
    if (!jobId) { setLedger(null); return; }
    setLoading(true);
    setError(null);
    ApiCall.get(`/reports/heat-ledger?jobId=${jobId}`)
      .then((r) => setLedger(r.data?.data))
      .catch(() => setError("Failed to load Heat Ledger Report"))
      .finally(() => setLoading(false));
  }, [jobId]);

  return (
    <>
      <Helmet><title>Heat Ledger Report | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HomeRounded fontSize="small" />
              <Typography variant="body2" color="text.secondary">Reports</Typography>
            </Stack>
            <Typography variant="body2" color="text.primary" fontWeight={600}>Heat Ledger</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            {ledger && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRounded />}
                onClick={() => exportCSV(jobId, ledger.ledger)}
              >
                Export CSV
              </Button>
            )}
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/reports")}
            >
              All Reports
            </Button>
          </Stack>
        </Stack>
        <PageGuide
          why="Trace every heat number to the pieces and jobs it went into."
          who="QC and the QM, tracing heats to the pieces and jobs they went into."
          what="A ledger of material heats and where each was used."
          how="Filter by job or heat, then export."
          when="For material traceability and audit support."
          standard="AISC §2.5 material traceability"
        />

        {/* ── job selector ── */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Job</InputLabel>
            <Select
              value={jobId}
              label="Select Job"
              onChange={(e) => setJobId(e.target.value)}
            >
              {jobs.map((j) => (
                <MenuItem key={j.id} value={j.id}>
                  {j.jobNumber ? `${j.jobNumber} — ${j.projectName || j.name || "Untitled"}` : (j.projectName || j.name || `Job #${j.id}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* ── states ── */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading heat ledger…</Typography>
          </Box>
        )}

        {!loading && !ledger && !error && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography color="text.secondary">Select a job to view its heat receipt and issue ledger.</Typography>
          </Box>
        )}

        {/* ── summary chips ── */}
        {ledger && !loading && (
          <>
            <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
              <Chip label={`${ledger.totalHeats} Heat${ledger.totalHeats !== 1 ? "s" : ""}`} color="primary" size="small" />
              <Chip
                label={`${ledger.ledger.filter((h) => h.cmtrVerified).length} CMTR Verified`}
                color="success"
                variant="outlined"
                size="small"
              />
              <Chip
                label={`${ledger.ledger.filter((h) => !h.cmtrVerified).length} Unverified`}
                color="warning"
                variant="outlined"
                size="small"
              />
            </Stack>

            {ledger.ledger.length === 0 ? (
              <Alert severity="info">No heats found for this job.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <SmartTable tableId="report-heat-ledger" lockedColumns={["Heat #", "CMTR", "Status"]} size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell><strong>Heat #</strong></TableCell>
                      <TableCell><strong>Grade</strong></TableCell>
                      <TableCell><strong>Shape</strong></TableCell>
                      <TableCell><strong>Supplier</strong></TableCell>
                      <TableCell align="right"><strong>Received Qty</strong></TableCell>
                      <TableCell align="right"><strong>Issued Pieces</strong></TableCell>
                      <TableCell><strong>CMTR</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Received</strong></TableCell>
                      <TableCell><strong>Released</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.ledger.map((h) => (
                      <TableRow key={h.id} hover>
                        <TableCell sx={{ fontWeight: 600, fontFamily: "monospace" }}>{h.heatNumber}</TableCell>
                        <TableCell>{h.grade || "—"}</TableCell>
                        <TableCell>{h.shape || "—"}</TableCell>
                        <TableCell>{h.supplier || "—"}</TableCell>
                        <TableCell align="right">{h.received_qty != null ? `${h.received_qty} ${h.unit || ""}`.trim() : "—"}</TableCell>
                        <TableCell align="right">{h.issued_piece_count}</TableCell>
                        <TableCell>
                          {h.cmtrVerified
                            ? <CheckCircle sx={{ color: "success.main", fontSize: 18 }} />
                            : <Cancel sx={{ color: "warning.main", fontSize: 18 }} />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={prettyStatus(h.status)}
                            color={STATUS_COLOR[h.status] || "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{h.receivedDate ? new Date(h.receivedDate).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>{h.releasedAt  ? new Date(h.releasedAt).toLocaleDateString()  : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </SmartTable>
              </TableContainer>
            )}
          </>
        )}
      </Container>
    </>
  );
}
